import { deleteHeaderToken, setHeaderToken } from '@/http';
import { router } from '@/router';
import { createLoginLogoutLog, getProfile, handleAxiosError, setFailedLogin } from '@/services/axiosService';
import { SUPERADMIN } from '@/services/roleService';

import { i18n } from '@/main';
import { getIdToken, signIn, signOut } from '../../services/firebaseService';
import { firebaseConfig } from '../../../configs/configFirebase.json';

// STATE
const state = {
    status: {
        loaded: false,
        sidebar: true
    },
    user: {},
    logoutMsg: null
};

// *** CLEAN LOCAL STORAGE ***
const cleanLocalStorage = () => {
    localStorage.removeItem('id');
    localStorage.removeItem('locale');
    localStorage.removeItem('pharmacy_id');
    localStorage.removeItem('token');
    localStorage.removeItem('user');
};

// *** SAVE USER PROFILE TO LOCAL STORAGE ***
const saveProfileToLocalStorage = profile => {
    localStorage.setItem('id', profile.id);
    localStorage.setItem('locale', profile.language);
    localStorage.setItem('pharmacy_id', profile.pharmacy_id);
    localStorage.setItem('user', JSON.stringify(profile));
};

// *** GET AND SET USER ID TOKEN ***
const setToken = async () => {
    const token = await getIdToken();
    localStorage.setItem('token', token);
    setHeaderToken(token);
};

// VUE STATE ACTIONS
const actions = {
    // GET USER
    async getUser({ commit }) {
        let user = localStorage.getItem('user');
        if (user) {
            // user profile in local storage
            user = JSON.parse(user);
            commit('login_success', user);
        } else {
            // user profile not in local storage -> get from backend
            try {
                const profile = await getProfile();
                saveProfileToLocalStorage(profile);
                commit('login_success', profile);
            } catch (error) {
                handleAxiosError(error);
                throw new Error();
            }
        }
    },
    // HIDE SIDEBAR
    hideSidebar({ commit }) {
        commit('hide_sidebar');
    },
    // LOGIN
    async login({ commit }, user) {
        try {
            try {
                // sign in using firebase authentication
                await signIn(user.email, user.password);
            } catch (error) {
                // handled failed authentication
                switch (error.code) {
                    case 'auth/too-many-requests': // to many login attempts
                        throw new Error('error_login_too_many_attempts');
                    case 'auth/user-not-found': // user not found by e-mail
                        throw new Error('error_login_user_not_found');
                    case 'auth/user-disabled': // user disabled
                    case 'auth/wrong-password': // wrong password
                        // if user disabled or wrong password entered, set failed login on backend
                        const result = await setFailedLogin({
                            email: user.email,
                            errorCode: error.code
                        });
                        switch (result) {
                            case 'enabled': // if user re-enabled, retry login
                                await actions.login({ commit }, user);
                                return;
                            case 'disabled': // user has been manually disabled
                                throw new Error('error_web_user_inactive');
                            case 'disabledTooManyAttempts': // user temporarily disabled (too many failed logins)
                                throw new Error('error_login_too_many_attempts');
                            case 'wrongPassword': // wrong password
                                throw new Error('error_login_wrong_password');
                        }
                    default:
                        throw new Error(error.message);
                }
            }
            commit('login_request');
            await setToken();
            const profile = await getProfile(); // get web user profile from backend
            if (firebaseConfig.authDomain === 'drugsafe-remomedi.firebaseapp.com' &&
                profile.pharmacy_id === '-MKyOSvkem3Ge7FwEOyJ') { //THIS SHOULD BE ONLY concerning the PROD. Syke should not use this domain
                throw new Error('error_switch_to_syke_mode');
            }
            i18n.locale = profile.language;
            saveProfileToLocalStorage(profile);
            // send login log after successful login
            await createLoginLogoutLog({
                action: 'login',
                date_and_time: new Date().toISOString(),
                full_name: `${profile.first_name} ${profile.last_name}`,
                pharmacy_id: profile.pharmacy_id ? profile.pharmacy_id : null,
                platform: profile.pharmacy_id ? 'webtool_pharmacy' : 'webtool_superadmin',
                user_id: profile.id
            });
            commit('login_success', profile);
            // route to my pharmacy page (pharmacy user) or pharmacy index (superadmin)
            if (profile.pharmacy_id) {
                router.push({ name: 'my_pharmacy' });
            } else {
                router.push({ name: 'pharmacy.index' });
            }
        } catch (error) {
            // failed login: clean local storage, delete header token, show error
            cleanLocalStorage();
            deleteHeaderToken();
            const errorMsg = error.response ? error.response.data.message : error.message;
            commit('login_error', errorMsg);
        }
    },
    // LOGOUT
    async logout({ commit }, { msg } = { msg: '' }) {
        if (state.status.loggedIn && !state.status.loggingOut) {
            commit('logging_out');
            if (state.user) {
                try {
                    // send logout log
                    await createLoginLogoutLog({
                        action: 'logout',
                        date_and_time: new Date().toISOString(),
                        full_name: `${state.user.first_name} ${state.user.last_name}`,
                        pharmacy_id: state.user.pharmacy_id ? state.user.pharmacy_id : null,
                        platform: state.user.pharmacy_id ? 'webtool_pharmacy' : 'webtool_superadmin',
                        user_id: state.user.id
                    });
                } catch (error) {
                    handleAxiosError(error);
                }
            }
            deleteHeaderToken();
            await signOut(); // sign out firebase auth user (revoke id and refresh token)
            commit('logout', msg);
            cleanLocalStorage();
            commit('loaded_true');
            await router.push({ name: 'login' }); // route to login page
        }
    },
    // REDIRECT TO LOGIN PAGE
    redirectToLogin({ commit }) {
        commit('token_not_set');
        router.push({ name: 'login' });
    },
    // SET LOADED_TRUE
    setLoadedTrue({ commit }) {
        commit('loaded_true');
    },
    // GET AND SET FIREBASE AUTH USER ID TOKEN
    async setToken() {
        await setToken();
    },
    // SHOW SIDEBAR
    show_sidebar({ commit }) {
        commit('show_sidebar');
    },
    // UPDATE WEB USER PROFILE FROM BACKEND AND SAVE IT IN LOCAL STORAGE
    async updateProfile({ commit }) {
        const profile = await getProfile();
        saveProfileToLocalStorage(profile);
        commit('set_user', profile);
    }
};

// VUE STATE GETTERS
const getters = {
    status: state => state.status,
    user: state => state.user,
    superadmin: state => {
        return state.user.role === SUPERADMIN;
    },
    permissions: state => state.user.permissions,
    logoutMsg: state => state.logoutMsg
};

// VUE STATE MUTATIONS
const mutations = {
    hide_sidebar(state) {
        state.status.sidebar = false;
    },
    loaded_true(state) {
        state.status.loaded = true;
    },
    logging_out(state) {
        state.status = {
            ...state.status,
            loggingOut: true
        };
    },
    login_request(state) {
        state.status = { loggingIn: true };
        state.logoutMsg = null;
    },
    login_error(state, message) {
        state.status = {
            loggedIn: false,
            loaded: true,
            error: true,
            message: message
        };
    },
    login_success(state, profile) {
        state.status = {
            loggedIn: true,
            loaded: true,
            sidebar: true
        };
        state.user = profile;
    },
    logout(state, logoutMsg) {
        state.status = {};
        state.token = 'test';
        state.user = null;
        state.logoutMsg = logoutMsg;
    },
    set_user(state, value) {
        state.user = value;
    },
    show_sidebar(state) {
        state.status.sidebar = true;
    },
    token_not_set(state) {
        state.status = {
            loggedIn: false,
            loaded: true
        };
    }
};

export default {
    namespaced: true,
    state,
    getters,
    actions,
    mutations
};
